import React from "react";
import { useTranslation } from "react-i18next";
import "./css/solutions.css";
import batteryHero from "./img/Battery1.webp";
import { ensureNamespace } from "../../i18n/ensurePageI18n";

const loadI18n = (i18n) => {
  ensureNamespace(i18n, 'battery', {
    en: {
      sectionTitle: "Lithium Battery",
      sectionSubtitle: "Surface Inspection Systems for Lithium Battery Materials",
      title: "LITHIUM BATTERY",
      heroDesc: "Wintriss web inspection systems detect 100% of surface defects in Battery Separator Films, Foils, and Electrodes. We also monitor key tolerances such as coating and substrate widths. Real-time processing of high-resolution imagery supports the highest quality goals.",
      typicalProductsTitle: "Typical Products",
      typicalProductsContent: "Battery separator films, foils, and electrodes",
      typicalDefectsTitle: "Typical Defects",
      typicalDefectsContent: "Bright spot, scratch, micro-scratch, gels, bumps, streaks, voids, stains, pinholes, crease, folds, contaminants, etc.",
      separatorTitle: "Battery Separator Films",
      separatorDefects: "Holes, oil spots, tears, delamination, bright spots, dark lines, stains, scratches, gels, roll impression, contaminants, and streaks.",
      electrodesTitle: "Battery Electrodes",
      electrodesDefects: "Scratches, wrinkles, missing coatings, missing carbon, dents, bumps, bright spots, black spots, bubbles, and fold lines.",
      aluminumTitle: "Aluminum Laminate Films",
      aluminumDefects: "Bumps, contaminants, stains, voids, air bubbles, wrinkles, scratches, dark spots, fish eyes, pin holes, and fold lines."
    },
    zh: {
      sectionTitle: "鋰電池",
      sectionSubtitle: "鋰電池材料表面檢測系統",
      title: "鋰電池",
      heroDesc: "微覺視網頁式檢測系統可對電池隔離膜、箔材與電極進行 100% 表面缺陷檢測，並監控塗佈與基材寬度等關鍵公差；高解析影像即時處理支援最高品質目標。",
      typicalProductsTitle: "典型產品",
      typicalProductsContent: "電池隔離膜、箔材與電極",
      typicalDefectsTitle: "常見缺陷",
      typicalDefectsContent: "亮點、刮傷、微刮傷、凝膠、突起、條紋、空隙、污漬、針孔、摺痕、褶皺與污染物等。",
      separatorTitle: "電池隔離膜",
      separatorDefects: "孔洞、油漬、撕裂、分層、亮點、黑線、污漬、刮傷、凝膠、壓痕、污染物與條紋。",
      electrodesTitle: "電池電極",
      electrodesDefects: "刮傷、皺紋、塗層缺失、碳層缺失、凹痕、突起、亮點、黑點、氣泡、摺線。",
      aluminumTitle: "鋁塑複合膜",
      aluminumDefects: "凸起、污染物、污漬、空隙、氣泡、皺紋、刮傷、黑點、魚眼、針孔、摺線。"
    }
  });
};

function Battery() {
  const { t, i18n } = useTranslation();
  loadI18n(i18n);

  return (
    <div>
      <section className="section-battery relative bg-gray-900 text-white">
        <div className="absolute inset-0 bg-black bg-opacity-70" />
        <div className="container mx-auto px-4 relative z-10 flex justify-between items-start">
          <div>
            <h1 className="text-5xl font-bold mb-4">{t("battery:sectionTitle")}</h1>
            <p className="text-xl text-white">{t("battery:sectionSubtitle")}</p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-2 gap-10 items-center">
        <div>
          <h2 className="text-3xl font-bold mb-4 tracking-wide">{t("battery:title")}</h2>
          <p className="leading-relaxed text-sm md:text-base">{t("battery:heroDesc")}</p>
        </div>
        <div className="flex justify-center">
          <img src={batteryHero} alt={t("battery:title")} className="rounded-lg shadow-lg w-full max-w-md object-cover" loading="lazy" />
        </div>
      </div>

      <section className="max-w-7xl mx-auto px-6 pb-20 grid md:grid-cols-4 gap-6">
        <div className="bg-white border rounded-lg p-5 shadow-sm">
          <h3 className="text-lg font-semibold mb-3">{t("battery:typicalProductsTitle")}</h3>
          <p className="text-sm text-gray-700 leading-relaxed">{t("battery:typicalProductsContent")}</p>
        </div>
        <div className="bg-white border rounded-lg p-5 shadow-sm">
          <h3 className="text-lg font-semibold mb-3">{t("battery:typicalDefectsTitle")}</h3>
          <p className="text-sm text-gray-700 leading-relaxed">{t("battery:typicalDefectsContent")}</p>
        </div>
        <div className="bg-white border rounded-lg p-5 shadow-sm">
          <h3 className="text-lg font-semibold mb-3">{t("battery:separatorTitle")}</h3>
          <p className="text-sm text-gray-700 leading-relaxed">{t("battery:separatorDefects")}</p>
        </div>
        <div className="bg-white border rounded-lg p-5 shadow-sm">
          <h3 className="text-lg font-semibold mb-3">{t("battery:electrodesTitle")}</h3>
          <p className="text-sm text-gray-700 leading-relaxed">{t("battery:electrodesDefects")}</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="bg-white border rounded-lg p-6 shadow-sm">
          <h3 className="text-lg font-semibold mb-3">{t("battery:aluminumTitle")}</h3>
          <p className="text-sm text-gray-700 leading-relaxed">{t("battery:aluminumDefects")}</p>
        </div>
      </section>
    </div>
  );
}

export default Battery;